// 시장 이슈의 '트렌드' 탭 데이터 — 키워드가 뉴스에서 얼마나 다뤄졌는지.
//
// 뉴스 목록만 보여주면 토픽 탭(뉴스를 묶어 요약한 결과)과 겹쳐 보인다. 뉴스는
// **시계열**로 볼 때만 새 정보가 된다 — 화제가 커지는 중인지, 반짝이었는지.
//
// 그리는 값은 count(기사 수)가 아니라 value(전체 뉴스 대비 비중)다. 전체 수집량이
// 늘면 count 도 같이 늘어서 화제성 변화를 count 로는 구분할 수 없다(news_trend.py 주석).
//
// 추이와 기사를 훅 두 개로 나눈 이유: 차트는 **조회 기간 전체**를 유지하고, 기사는
// 차트에서 드래그해 좁힌 **구간**을 따라가야 한다. 한 훅에 묶으면 구간을 좁힐 때마다
// 차트가 다시 그려져 방금 고른 구간이 화면에서 사라진다.
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

export interface TrendSummary {
  average?: number;
  latest?: number;
  peak_value?: number;
  peak_at?: string;
  articles?: number;
}

export interface TrendSeriesSummary extends TrendSummary {
  query: string;
}

export interface TrendRawSeries {
  query: string;
  /** 전체 뉴스 대비 비중(%) — 버킷이 없는 구간은 null */
  values: (number | null)[];
  /** 그 구간에 실제로 발행된 기사 수 */
  counts: (number | null)[];
}

export interface TrendData {
  query: string;
  /** 견주고 있는 키워드 — 하나여도 배열로 온다 */
  queries?: string[];
  interval: string;
  total_matches?: number;
  points?: number;
  /** vachart 가 그대로 먹는 차트 규격. zoom 이 켜져야 드래그로 구간을 좁힐 수 있다 */
  spec?: { zoom?: boolean; [k: string]: unknown };
  summary?: TrendSummary;
  /** 키워드별 요약 — 견줄 때 지표를 나눠 보여준다 */
  summaries?: TrendSeriesSummary[];
  /** 구간 키(차트 x 축) — 화면이 보이는 구간만 다시 계산할 때 쓴다 */
  x?: string[];
  /** 키워드별 원시 수치 */
  series?: TrendRawSeries[];
}

/** 화면이 보여줄 지표 — 해석이 필요한 비중(%) 대신 **건수와 방향**으로 말한다.
 *  "1만 건 중 226건" 같은 값은 읽는 사람이 한 번 더 환산해야 한다(Bishop). */
export interface TrendStats {
  /** 이 구간에 발행된 기사 수 */
  articles: number;
  /** 하루 평균 — 주기(일·주·월)를 바꿔도 견줄 수 있는 값이다 */
  perDay: number;
  /** 가장 많았던 구간 */
  peakAt: string;
  peakCount: number;
  from: string;
  to: string;
  /** 구간에 값이 하나도 없으면 지표를 그리지 않는다 */
  empty: boolean;
}

const DAY = 86400000;

/** x[i0]..x[i1] 구간의 지표. i1 을 넘기지 않으면 전체 구간이다. */
export function statsFor(
  x: string[], counts: (number | null)[], i0 = 0, i1 = x.length - 1,
): TrendStats {
  const lo = Math.max(0, Math.min(i0, i1));
  const hi = Math.min(x.length - 1, Math.max(i0, i1));
  const from = x[lo] || "";
  const to = x[hi] || "";
  let articles = 0;
  let peakAt = "";
  let peakCount = -1;
  let seen = 0;
  for (let i = lo; i <= hi; i++) {
    const c = counts[i];
    if (typeof c !== "number") continue;
    seen += 1;
    articles += c;
    if (c > peakCount) {
      peakCount = c;
      peakAt = x[i];
    }
  }
  // 구간 길이는 마지막 버킷의 폭을 알 수 없어 키 간격으로 어림한다 — 하루 평균은
  // 자릿수 감을 주는 값이라 이 정도 오차는 뜻을 바꾸지 않는다.
  const days = from && to
    ? Math.max(1, Math.round((new Date(to).getTime() - new Date(from).getTime()) / DAY) + 1)
    : 1;
  return {
    articles,
    perDay: articles / days,
    peakAt,
    peakCount: Math.max(0, peakCount),
    from,
    to,
    empty: seen === 0,
  };
}

/** 날짜(차트 x 값) → 인덱스. 없으면 가장 가까운 자리로 — 드래그가 준 값이 버킷 키와
 *  정확히 같지 않을 수 있다. */
export function indexOfDate(x: string[], date: string, fallback: number): number {
  if (!date) return fallback;
  const i = x.indexOf(date);
  if (i >= 0) return i;
  const t = new Date(date).getTime();
  if (Number.isNaN(t)) return fallback;
  let best = fallback;
  let gap = Infinity;
  x.forEach((k, idx) => {
    const g = Math.abs(new Date(k).getTime() - t);
    if (g < gap) {
      gap = g;
      best = idx;
    }
  });
  return best;
}

export interface TrendNews {
  id?: string;
  title?: string;
  publisher?: string;
  summary?: string;
  author?: string;
  section?: string;
  /** 서버 news_trend.search 가 주는 필드명 그대로 */
  at?: string;
  url?: string;
  thumb?: string;
}

/** 집계 주기 — 서버 news_trend.INTERVALS 와 같은 값이다 */
export const TREND_INTERVALS: { key: string; label: string }[] = [
  { key: "1d", label: "일별" },
  { key: "1w", label: "주별" },
  { key: "1M", label: "월별" },
];

/** haystack 뉴스 데이터 시작 시점 — 이보다 이른 날은 고를 수 없다 */
export const DATA_START = "1990-01-01";

/** 구간 길이에 맞는 기본 주기 — 10년을 일별로 그리면 점이 3600개다.
 *  사용자가 직접 고르면 그 값이 우선이고, 이건 고르기 전의 기본값이다. */
export function autoInterval(from: string, to: string): string {
  if (!from || !to) return "1w";
  const days = Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000);
  return days > 1100 ? "1M" : days > 300 ? "1w" : "1d";
}

export interface TrendState {
  data: TrendData | null;
  loading: boolean;
  error: string;
}

/** 언급 추이(차트). 키워드·기간·주기가 바뀌면 다시 받는다.
 *
 *  키워드는 최대 5개까지 함께 넘긴다 — 한 차트에 계열로 나란히 그린다(서버가 병렬 조회).
 *  요청이 겹칠 수 있어(테마를 빠르게 눌러 볼 때) 마지막 요청만 반영한다.
 */
export function useTrend(
  keywords: string[], from: string, to: string, interval = "",
): TrendState {
  const [data, setData] = useState<TrendData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const seq = useRef(0);

  // 배열은 렌더마다 새 참조라 그대로 의존성에 넣으면 매번 다시 받는다 — 값으로 비교한다
  const key = keywords.map((k) => k.trim()).filter(Boolean).join("\u0000");

  const load = useCallback(async () => {
    const qs = key ? key.split("\u0000") : [];
    if (!qs.length) {
      setData(null);
      setError("");
      return;
    }
    if (!from || !to) return;
    const iv = interval || autoInterval(from, to);
    const mine = ++seq.current;
    setLoading(true);
    setError("");
    try {
      const t = await api<{ ok: boolean; data?: TrendData; message?: string }>(
        `/agent/news-trend?${qs.map((q) => `query=${encodeURIComponent(q)}`).join("&")}`
        + `&interval=${iv}&date_from=${from}&date_to=${to}`,
      );
      if (mine !== seq.current) return;
      if (!t.ok || !t.data) throw new Error(t.message || "추이를 가져오지 못했습니다.");
      setData(t.data);
    } catch (e) {
      if (mine !== seq.current) return;
      setError(e instanceof Error ? e.message : "추이를 가져오지 못했습니다.");
      setData(null);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [key, from, to, interval]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error };
}

/** 함께 언급된 기업 · 분야 분포 — **표본**이다.
 *
 *  haystack 에 집계가 없어(aggs·facets·group_by 전부 무시, /news/_aggs 는 400)
 *  받아온 문서 안에서 센다. 절대 건수가 아니라 순위로만 읽어야 해서 화면이
 *  "최근 N건 기준" 이라고 밝힌다.
 */
export interface TrendFacetCompany {
  name: string;
  symbol?: string;
  exchange?: string | null;
  count: number;
}
export interface TrendFacetSection {
  key: string;
  label: string;
  count: number;
  pct: number;
}
export interface TrendFacets {
  companies: TrendFacetCompany[];
  sections: TrendFacetSection[];
  /** 실제로 센 문서 수 — 요청한 수보다 적을 수 있다(비슷한 기사를 묶어 걸러낸다) */
  sampled: number;
  loading: boolean;
}

export function useTrendFacets(keyword: string, from: string, to: string): TrendFacets {
  const [data, setData] = useState<{ companies: TrendFacetCompany[]; sections: TrendFacetSection[]; sampled: number }>(
    { companies: [], sections: [], sampled: 0 },
  );
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const q = keyword.trim();
    if (!q || !from || !to) {
      setData({ companies: [], sections: [], sampled: 0 });
      return;
    }
    const mine = ++seq.current;
    setLoading(true);
    void api<{ ok: boolean; data?: { companies?: TrendFacetCompany[]; sections?: TrendFacetSection[]; sampled?: number } }>(
      `/agent/news-facets?query=${encodeURIComponent(q)}&date_from=${from}&date_to=${to}`,
    )
      .then((d) => {
        if (mine !== seq.current) return;
        setData({
          companies: d.data?.companies || [],
          sections: d.data?.sections || [],
          sampled: d.data?.sampled || 0,
        });
      })
      // 보조 정보다 — 실패해도 화면은 그대로 두고 그 칸만 비운다
      .catch(() => {
        if (mine === seq.current) setData({ companies: [], sections: [], sampled: 0 });
      })
      .finally(() => {
        if (mine === seq.current) setLoading(false);
      });
  }, [keyword, from, to]);

  return { ...data, loading };
}

/** 번호 페이저에 그릴 항목 — 1 … 4 5 6 … 10.
 *
 *  10개를 다 늘어놓으면 좁은 패널에서 번호가 화면을 다 먹는다(Bishop). 양 끝과
 *  현재 주변만 남기고 사이는 줄임표로 접는다. 최대 7칸이라 폭이 흔들리지 않는다.
 */
export function pageItems(
  current: number, total: number, edge = 1, around = 1,
): (number | "…")[] {
  const keep = new Set<number>();
  for (let i = 1; i <= edge; i++) {
    keep.add(i);
    keep.add(total - i + 1);
  }
  for (let i = current - around; i <= current + around; i++) keep.add(i);
  const nums = [...keep].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  nums.forEach((n, i) => {
    // 건너뛴 자리에만 줄임표 — 딱 한 칸 비었으면 그 번호를 그냥 보여준다
    if (i && n - nums[i - 1] === 2) out.push(n - 1);
    else if (i && n - nums[i - 1] > 2) out.push("…");
    out.push(n);
  });
  return out;
}

export interface TrendNewsState {
  items: TrendNews[];
  /** 조건에 맞는 전체 기사 수(클러스터링 전) — 목록 건수와는 다르다 */
  total: number | null;
  hasNext: boolean;
  /** 번호 페이저가 그릴 페이지 수(서버 상한까지) */
  pages: number;
  /** 서버가 더 깊은 페이지를 보장하지 못하는 상태 — 화면이 알려야 한다 */
  capped: boolean;
  loading: boolean;
  error: string;
}

/** 차트에서 드래그하면 이 훅의 구간이 바뀐다 — 드래그 중에는 이벤트가 연속으로
 *  오므로 잠깐 묶었다가 한 번만 조회한다(멈춘 뒤 조회). */
const NEWS_SETTLE_MS = 320;

/** 고른 구간의 기사 — 구간·키워드·페이지가 바뀌면 다시 받는다. */
export function useTrendNews(
  keyword: string, from: string, to: string, page: number,
): TrendNewsState {
  const [items, setItems] = useState<TrendNews[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [hasNext, setHasNext] = useState(false);
  const [pages, setPages] = useState(1);
  const [capped, setCapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const seq = useRef(0);

  useEffect(() => {
    const q = keyword.trim();
    if (!q || !from || !to) {
      setItems([]);
      setTotal(null);
      return;
    }
    const mine = ++seq.current;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      setError("");
      try {
        const d = await api<{
          ok: boolean;
          data?: {
            items?: TrendNews[]; total_matches?: number | null;
            has_next?: boolean; capped?: boolean; pages?: number;
          };
          message?: string;
        }>(
          `/agent/news-search?query=${encodeURIComponent(q)}`
          + `&date_from=${from}&date_to=${to}&page=${page}`,
        );
        if (mine !== seq.current) return;
        if (!d.ok || !d.data) throw new Error(d.message || "기사를 가져오지 못했습니다.");
        setItems(d.data.items || []);
        setTotal(d.data.total_matches ?? null);
        setHasNext(!!d.data.has_next);
        setPages(Math.max(1, d.data.pages || 1));
        setCapped(!!d.data.capped);
      } catch (e) {
        if (mine !== seq.current) return;
        setError(e instanceof Error ? e.message : "기사를 가져오지 못했습니다.");
        setItems([]);
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, NEWS_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [keyword, from, to, page]);

  return { items, total, hasNext, pages, capped, loading, error };
}
